import {
  MapPin, Check, Building2, Banknote, Handshake, Building,
  ClipboardCheck, ShieldCheck, Globe, Scale, Quote,
} from 'lucide-react';
import { Reveal, SectionLabel } from './Reveal';
import { LEAD8, MCCARTHY } from '../../data/content';
import lead8Img from '../../assets/lead8.png';

const LAW_ICONS = {
  infra: Building2,
  finance: Banknote,
  corporate: Handshake,
  realestate: Building,
  regulatory: ClipboardCheck,
  risk: ShieldCheck,
  international: Globe,
  publiclaw: Scale,
};

const Lead8Block = () => (
  <div className="mt-14 border border-white/10 bg-navy-950 p-8 lg:p-12">
    <Reveal>
      <div className="flex flex-col justify-between gap-4 border-b border-white/10 pb-8 md:flex-row md:items-end">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-gold">
            Featured Partner · Design & Masterplanning
          </span>
          <div className="mt-3 flex items-baseline gap-4">
            <span className="font-serif text-4xl tracking-tight text-white lg:text-5xl">
              {LEAD8.name}
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400">
              {LEAD8.tagline}
            </span>
          </div>
        </div>
        <p className="max-w-md text-base leading-relaxed text-slate-300">
          {LEAD8.description}
        </p>
      </div>
    </Reveal>

    <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-3 lg:grid-cols-5">
      {LEAD8.facts.map((s, i) => (
        <Reveal key={i} delay={i * 0.05}>
          <div className="h-full bg-navy-950 p-6">
            <div className="font-serif text-3xl leading-none text-gold">{s.value}</div>
            <div className="mt-3 text-xs leading-relaxed text-slate-400">{s.label}</div>
          </div>
        </Reveal>
      ))}
    </div>

    <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-2">
      <Reveal>
        <h4 className="text-xs font-bold uppercase tracking-[0.24em] text-gold">
          Select Global Projects
        </h4>
        <ul className="mt-6 divide-y divide-white/10 border-y border-white/10">
          {LEAD8.projects.map((p) => (
            <li key={p.name} className="flex items-start gap-3 py-4">
              <MapPin className="mt-1 h-4 w-4 shrink-0 text-gold/70" strokeWidth={2} />
              <div>
                <div className="font-serif text-lg text-white">{p.name}</div>
                <div className="text-xs uppercase tracking-[0.16em] text-slate-500">{p.place}</div>
              </div>
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.1}>
        <h4 className="text-xs font-bold uppercase tracking-[0.24em] text-gold">
          What Sets Lead8 Apart
        </h4>
        <ul className="mt-6 space-y-4">
          {LEAD8.differentiators.map((d) => (
            <li key={d} className="flex gap-3">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
              <span className="text-sm leading-relaxed text-slate-300">{d}</span>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>

    <Reveal delay={0.05}>
      <figure className="mt-12 border border-white/10 bg-navy-900 p-4 lg:p-6">
        <img src={lead8Img} alt="Lead8 — global track record & awards" className="w-full" />
        <figcaption className="mt-3 text-center text-[11px] uppercase tracking-[0.2em] text-slate-500">
          Lead8 — Global Track Record & Awards
        </figcaption>
      </figure>
    </Reveal>

    <Reveal>
      <h4 className="mt-12 text-center text-xs font-bold uppercase tracking-[0.24em] text-gold">
        Trusted by leading organisations globally
      </h4>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {LEAD8.trustedBy.map((t) => (
          <span
            key={t}
            className="border border-white/10 px-4 py-2 text-sm text-slate-300 transition-colors duration-300 hover:border-gold/40 hover:text-white"
          >
            {t}
          </span>
        ))}
      </div>
    </Reveal>

    <Reveal delay={0.1}>
      <p className="mx-auto mt-12 max-w-3xl text-center font-serif text-xl italic leading-snug text-white/90 lg:text-2xl text-balance">
        {LEAD8.closing}
      </p>
    </Reveal>
  </div>
);

const McCarthyBlock = () => (
  <div className="mt-8 border border-white/10 bg-navy-950 p-8 lg:p-12">
    <Reveal>
      <div className="flex flex-col justify-between gap-4 border-b border-white/10 pb-8 md:flex-row md:items-end">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-gold">
            Featured Partner · {MCCARTHY.sector}
          </span>
          <div className="mt-3 flex flex-wrap items-baseline gap-4">
            <span className="font-serif text-4xl tracking-tight text-white lg:text-5xl">
              {MCCARTHY.name}
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400">
              {MCCARTHY.tagline}
            </span>
          </div>
          <div className="mt-3 text-sm text-slate-400">
            <span className="text-white">{MCCARTHY.person}</span> — {MCCARTHY.role}
          </div>
        </div>
        <p className="max-w-md text-base leading-relaxed text-slate-300">
          {MCCARTHY.description}
        </p>
      </div>
    </Reveal>

    {/* Key credentials */}
    <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-3 lg:grid-cols-5">
      {MCCARTHY.credentials.map((s, i) => (
        <Reveal key={i} delay={i * 0.05}>
          <div className="h-full bg-navy-950 p-6">
            <div className="font-serif text-2xl leading-none text-gold lg:text-3xl">{s.value}</div>
            <div className="mt-3 text-xs leading-relaxed text-slate-400">{s.label}</div>
          </div>
        </Reveal>
      ))}
    </div>

    {/* Areas of expertise */}
    <Reveal>
      <h4 className="mt-12 text-xs font-bold uppercase tracking-[0.24em] text-gold">
        Areas of Expertise
      </h4>
    </Reveal>
    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {MCCARTHY.expertise.map((e, i) => {
        const Icon = LAW_ICONS[e.icon];
        return (
          <Reveal key={e.title} delay={(i % 4) * 0.06}>
            <div className="group flex h-full flex-col border border-white/10 bg-navy-900 p-6 transition-colors duration-500 hover:border-gold/40">
              <Icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
              <h5 className="mt-4 font-serif text-lg leading-tight text-white">{e.title}</h5>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{e.text}</p>
            </div>
          </Reveal>
        );
      })}
    </div>

    {/* Firm facts + mandate */}
    <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-2">
      <Reveal>
        <h4 className="text-xs font-bold uppercase tracking-[0.24em] text-gold">
          McCarthy Denning LLP
        </h4>
        <ul className="mt-6 space-y-4">
          {MCCARTHY.firm.map((d) => (
            <li key={d} className="flex gap-3">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
              <span className="text-sm leading-relaxed text-slate-300">{d}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[11px] italic text-slate-500">{MCCARTHY.note}</p>
      </Reveal>

      <Reveal delay={0.1}>
        <h4 className="text-xs font-bold uppercase tracking-[0.24em] text-gold">
          Role on the Global Healthcare City
        </h4>
        <p className="mt-6 text-base leading-relaxed text-slate-300">{MCCARTHY.mandate}</p>
      </Reveal>
    </div>

    {/* Quote */}
    <Reveal delay={0.1}>
      <div className="mx-auto mt-12 max-w-3xl text-center">
        <Quote className="mx-auto h-7 w-7 text-gold/60" strokeWidth={1.5} />
        <p className="mt-4 font-serif text-xl italic leading-snug text-white/90 lg:text-2xl text-balance">
          {MCCARTHY.quote}
        </p>
        <p className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-gold">
          {MCCARTHY.person}
        </p>
      </div>
    </Reveal>
  </div>
);

export const Partners = () => (
  <section
    id="partners"
    data-testid="partners-section"
    className="relative border-t border-white/5 bg-navy-900 py-24 lg:py-32"
  >
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="max-w-2xl">
        <Reveal>
          <SectionLabel>Our Partners</SectionLabel>
          <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl">
            Aligned with institutions that matter.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-300">
            We collaborate with world-class organisations across design,
            masterplanning and law to deliver at the highest standard.
          </p>
        </Reveal>
      </div>

      <Lead8Block />
      <McCarthyBlock />
    </div>
  </section>
);
